import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './jwt-decoder.component.module.scss';

interface JwtState {
    input: string;
    header: string;
    payload: string;
    signature: string;
    expStatus: 'expired' | 'valid' | 'none' | null;
    expDate: string;
    error: string;
}

function b64Decode(str: string): string {
    const padded = str.replace(/-/g, '+').replace(/_/g, '/');
    return atob(padded);
}

function toHex(str: string): string {
    return Array.from(str, c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
}

export default class JwtDecoderComponent extends Component<{ feature?: string }, JwtState> {
    state: JwtState = { input: '', header: '', payload: '', signature: '', expStatus: null, expDate: '', error: '' };

    decode = (input: string) => {
        this.setState({ input });
        const trimmed = input.trim();
        if (!trimmed) {
            this.setState({ header: '', payload: '', signature: '', expStatus: null, expDate: '', error: '' });
            return;
        }
        const parts = trimmed.split('.');
        if (parts.length !== 3) {
            this.setState({ header: '', payload: '', signature: '', expStatus: null, error: 'Invalid JWT: expected 3 dot-separated parts' });
            return;
        }
        try {
            const header = JSON.stringify(JSON.parse(b64Decode(parts[0])), null, 2);
            const payloadObj = JSON.parse(b64Decode(parts[1]));
            const payload = JSON.stringify(payloadObj, null, 2);
            const signature = toHex(b64Decode(parts[2]));

            let expStatus: JwtState['expStatus'] = 'none';
            let expDate = '';
            if (payloadObj.exp) {
                const d = new Date(payloadObj.exp * 1000);
                expDate = d.toISOString();
                expStatus = d < new Date() ? 'expired' : 'valid';
            }

            this.setState({ header, payload, signature, expStatus, expDate, error: '' });
        } catch {
            this.setState({ header: '', payload: '', signature: '', expStatus: null, error: 'Failed to decode JWT. Check the format.' });
        }
    };

    render() {
        const { input, header, payload, signature, expStatus, expDate, error } = this.state;

        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Paste JWT</span>
                            <button type="button" onClick={() => this.decode('eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkphbmUgRG9lIiwiaWF0IjoxNzAwMDAwMDAwLCJleHAiOjE5MDAwMDAwMDB9.X6lpQK_rGcsk-vmKlcZpKt1V7G1lCu8DvDxK-G88Q9c')}
                                style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}>
                                Try sample
                            </button>
                        </label>
                        <textarea className={sharedStyles.textarea} value={input}
                            onChange={e => this.decode(e.target.value)}
                            placeholder="eyJhbGciOiJIUzI1NiIs..." rows={3} />
                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}

                        {header && (
                            <div className={styles.sections}>
                                <div className={styles.headerSection}>
                                    <div className={`${styles.sectionTitle} ${styles.headerTitle}`}>Header</div>
                                    <pre className={styles.json}>{header}</pre>
                                </div>
                                <div className={styles.payloadSection}>
                                    <div className={`${styles.sectionTitle} ${styles.payloadTitle}`}>Payload</div>
                                    <pre className={styles.json}>{payload}</pre>
                                    {expStatus === 'expired' && (
                                        <span className={`${styles.expiry} ${styles.expired}`}>⚠ Expired: {expDate}</span>
                                    )}
                                    {expStatus === 'valid' && (
                                        <span className={`${styles.expiry} ${styles.valid}`}>✓ Valid until: {expDate}</span>
                                    )}
                                    {expStatus === 'none' && (
                                        <span className={`${styles.expiry} ${styles.noExp}`}>No expiration claim</span>
                                    )}
                                </div>
                                <div className={styles.signatureSection}>
                                    <div className={`${styles.sectionTitle} ${styles.signatureTitle}`}>Signature (hex)</div>
                                    <pre className={styles.json}>{signature}</pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            </FullscreenHost>
        );
    }
}
